import { useFormat, useLocale, useTranslation } from '@dreamstack/i18n'
import type {
  LexiconArticlePreviewFragment,
  OtherNewsArticleFragment,
} from '@dreamstack/investors-graphql'
import type { FunctionComponent } from 'react'
import 'twin.macro'
import tw from 'twin.macro'
import type { NewsType } from '../index';
import { ContinueReadingLink, HeadingH3, HeadingH4 } from '../index'
import { PageTag } from '../layout/PageTag'
import { getLexiconPath } from '../lexicon/getLexiconPath'
import { getNewsPath } from './getNewsPath'

const ArticleWrapper = tw.div`
  md:(pr-px-64)
`

const FeaturedLexiconArticle = ({
  article,
  href,
}: {
  article: LexiconArticlePreviewFragment
  href: string
}) => {
  return (
    <ArticleWrapper>
      <div tw="mt-px-48 md:(mt-px-64) lg:(mt-px-80)">
        <HeadingH3
          tw="mt-px-24 lg:(mt-px-32) hyphens[auto]"
          dangerouslySetInnerHTML={{ __html: article.title ?? '' }}
        />
      </div>
      <div tw="mt-px-24 lg:(mt-px-32)">{article.previewText}</div>
      <ContinueReadingLink href={href} />
    </ArticleWrapper>
  )
}

const FeaturedNewsArticle = ({
  newsType,
  article,
  href,
  date,
}: {
  newsType: NewsType | string
  article: OtherNewsArticleFragment
  href: string
  date: string | undefined | null
}) => {
  return (
    <ArticleWrapper>
      <div tw="mt-px-48 md:(mt-px-64) lg:(mt-px-80)">
        <PageTag text={newsType}></PageTag>
      </div>
      <HeadingH3
        tw="mt-px-24 lg:(mt-px-32) hyphens[auto]"
        dangerouslySetInnerHTML={{ __html: article.title ?? '' }}
      />
      {date && !article.hideDate && <HeadingH4 tw="mt-px-16">{date}</HeadingH4>}
      <div tw="mt-px-24 lg:(mt-px-32)">{article.previewText}</div>
      <ContinueReadingLink href={href} file={article.file} />
    </ArticleWrapper>
  )
}

export const FeaturedArticleTemplate: FunctionComponent<React.PropsWithChildren<{
  article: OtherNewsArticleFragment | LexiconArticlePreviewFragment
}>> = ({ article }) => {
  const t = useTranslation()
  const formatDate = useFormat()
  const locale = useLocale()

  const { slug } = article
  if (!slug) return null

  if (article.__typename === 'LexiconArticle') {
    const { lexiconLetter } = article
    if (!lexiconLetter) return null
    const lexiconPath = getLexiconPath({ locale, slug, lexiconLetter })
    return <FeaturedLexiconArticle article={article} href={lexiconPath} />
  }

  if (article.__typename === 'NewsArticle') {
    const newsType: NewsType = article?.newsType as NewsType //newsType is set in contentful and should be a valid NewsType
    const translatedNewsType = t(`accentro:newsTypes.${article?.newsType}`)

    const formattedOtherArticlesDate = article.date
      ? formatDate(article.date, 'dd. MMMM yyyy')
      : null

    const newsPath = getNewsPath({ locale, slug, newsType })

    return (
      <FeaturedNewsArticle
        newsType={translatedNewsType}
        article={article}
        date={formattedOtherArticlesDate}
        href={newsPath}
      />
    )
  }
  return null
}
